(function (global) {
  'use strict';

  const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const keyPattern = /^[a-z][a-z0-9_-]*$/;
  const blocked = new Set(['__proto__', 'prototype', 'constructor']);
  const text = (value) => typeof value === 'string' ? value.trim() : '';
  const cents = (value) => Number.isSafeInteger(value) && value >= 0;
  const validKey = (value) => typeof value === 'string' && keyPattern.test(value) && !blocked.has(value);
  const propertyName = (value) => text(value) !== '' && !blocked.has(text(value));
  const invalidPrice = () => ({valid: false, priceCents: null, variantId: null});

  function normalize(input) {
    if (typeof input === 'string') {
      try { input = JSON.parse(input); } catch (_) { input = null; }
    }
    const output = {version: 1, valid: false, groups: [], regional_service: null, custom_color: null, errors: []};
    if (!isRecord(input) || input.version !== 1 || !Array.isArray(input.groups)) {
      output.errors.push('config');
      return output;
    }
    const keys = new Set(), properties = new Set();
    input.groups.forEach((group) => {
      if (!isRecord(group) || !validKey(group.key) || keys.has(group.key)
        || !text(group.label) || !propertyName(group.property_name)
        || properties.has(text(group.property_name)) || !['select', 'text'].includes(group.type)) {
        output.errors.push('group');
        return;
      }
      keys.add(group.key); properties.add(text(group.property_name));
      const item = {key: group.key, label: text(group.label), property_name: text(group.property_name),
        type: group.type, required: group.required === true, default: text(group.default),
        placeholder: text(group.placeholder), notice: text(group.notice), values: [], max_length: 120};
      if (group.type === 'text') {
        if (owns(group, 'max_length')) {
          if (!Number.isSafeInteger(group.max_length) || group.max_length < 1 || group.max_length > 500) output.errors.push('max_length');
          else item.max_length = group.max_length;
        }
        if (item.default.length > item.max_length) output.errors.push('default');
      } else if (!Array.isArray(group.values) || group.values.length === 0) {
        output.errors.push('values');
      } else {
        const ids = new Set();
        group.values.forEach((value) => {
          if (!isRecord(value) || !validKey(value.id) || ids.has(value.id) || !text(value.label)
            || !text(value.property_value) || !cents(value.surcharge_cents)) {
            output.errors.push('value');
            return;
          }
          ids.add(value.id);
          const entry = {id: value.id, label: text(value.label), property_value: text(value.property_value),
            surcharge_cents: value.surcharge_cents, inquiry_only: value.inquiry_only === true, constraints: {}};
          if (value.constraints !== undefined) {
            if (!isRecord(value.constraints)) output.errors.push('constraint');
            else ['max_width_cm', 'max_height_cm'].forEach((key) => {
              if (!owns(value.constraints, key)) return;
              if (typeof value.constraints[key] !== 'number' || !Number.isFinite(value.constraints[key]) || value.constraints[key] <= 0) output.errors.push('constraint');
              else entry.constraints[key] = value.constraints[key];
            });
          }
          item.values.push(entry);
        });
        if (item.default && !ids.has(item.default)) output.errors.push('default');
      }
      output.groups.push(item);
    });
    if (input.regional_service !== undefined && input.regional_service !== null) {
      const service = input.regional_service;
      if (!isRecord(service) || !text(service.label) || !text(service.notice)
        || !cents(service.surcharge_cents) || service.inquiry_only !== true) output.errors.push('regional_service');
      else output.regional_service = {label: text(service.label), notice: text(service.notice), surcharge_cents: service.surcharge_cents, inquiry_only: true};
    }
    if (input.custom_color !== undefined && input.custom_color !== null) {
      const color = input.custom_color;
      if (!isRecord(color) || !propertyName(color.property_name) || !cents(color.surcharge_cents)
        || color.inquiry_only !== true) output.errors.push('custom_color');
      else output.custom_color = {property_name: text(color.property_name), surcharge_cents: color.surcharge_cents, inquiry_only: true};
    }
    output.valid = output.errors.length === 0;
    return output;
  }

  function resolve(input, selections, context) {
    const config = normalize(input), values = isRecord(selections) ? selections : {}, state = isRecord(context) ? context : {};
    const result = {valid: false, surchargeCents: 0, properties: {}, requiredMissing: [], errors: [], inquiryRequired: false, selected: {}};
    if (!config.valid) { result.errors.push('config'); return result; }
    config.groups.forEach((group) => {
      const raw = owns(values, group.key) ? values[group.key] : group.default;
      const value = text(raw);
      if (value === '') {
        if (group.required) result.requiredMissing.push(group.key);
        return;
      }
      if (group.type === 'text') {
        if (typeof raw !== 'string' || value.length > group.max_length || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(value)) result.errors.push(group.key);
        else { result.properties[group.property_name] = value; result.selected[group.key] = value; }
        return;
      }
      const option = group.values.find((entry) => entry.id === value);
      if (!option) { result.errors.push(group.key); return; }
      result.selected[group.key] = option.id;
      result.properties[group.property_name] = option.property_value;
      result.surchargeCents += option.surcharge_cents;
      if (option.inquiry_only) result.inquiryRequired = true;
      [['max_width_cm', 'width'], ['max_height_cm', 'height']].forEach(([limit, dimension]) => {
        if (!owns(option.constraints, limit)) return;
        if (typeof state[dimension] !== 'number' || !Number.isFinite(state[dimension])
          || state[dimension] <= 0 || state[dimension] > option.constraints[limit]) result.errors.push(group.key + ':' + limit);
      });
    });
    if (state.customHex !== undefined && state.customHex !== null && state.customHex !== '') {
      const hex = text(state.customHex);
      if (!config.custom_color || !/^#[0-9a-f]{6}$/i.test(hex)) result.errors.push('custom_hex');
      else {
        result.properties[config.custom_color.property_name] = hex.toUpperCase();
        result.surchargeCents += config.custom_color.surcharge_cents;
        result.inquiryRequired = true;
      }
    }
    if (!cents(result.surchargeCents)) result.errors.push('surcharge');
    result.valid = result.requiredMissing.length === 0 && result.errors.length === 0;
    return result;
  }

  function resolveFlat(pricing, basePriceCents, baseVariantId, surchargeCents) {
    const surcharge = surchargeCents === undefined ? 0 : surchargeCents;
    const id = String(baseVariantId || '');
    if (!cents(basePriceCents) || !cents(surcharge) || !/^[1-9][0-9]*$/.test(id)) return invalidPrice();
    const priceCents = basePriceCents + surcharge;
    if (!cents(priceCents)) return invalidPrice();
    if (surcharge === 0 && !pricing) return {valid: true, priceCents, variantId: id};
    if (!isRecord(pricing) || pricing.version !== 1 || pricing.rule !== 'flat_plus_options'
      || pricing.base_price_cents !== basePriceCents || !isRecord(pricing.variant_map)
      || !owns(pricing.variant_map, id) || !isRecord(pricing.variant_map[id])
      || !owns(pricing.variant_map[id], String(priceCents))) return invalidPrice();
    const totalVariantId = String(pricing.variant_map[id][String(priceCents)]);
    return /^[1-9][0-9]*$/.test(totalVariantId)
      ? {valid: true, priceCents, variantId: totalVariantId} : invalidPrice();
  }

  function restrictToProduct(input, handle) {
    if (!/^plissee-(50|75|100)-blickdicht$/.test(String(handle || '')) || !isRecord(input)) return input;
    const config = JSON.parse(JSON.stringify(input));
    const structures = handle === 'plissee-100-blickdicht' ? ['pleated', 'honeycomb'] : ['pleated'];
    (config.groups || []).forEach((group) => {
      if (group.key === 'mounting') {
        group.values = (group.values || []).filter((value) => value.id === 'screw');
        group.default = 'screw'; group.notice = '';
      } else if (group.key === 'fabric_structure') {
        group.values = (group.values || []).filter((value) => structures.includes(value.id));
        group.default = structures.includes('honeycomb') && group.values.some((value) => value.id === 'honeycomb')
          ? 'honeycomb' : 'pleated';
      }
    });
    return config;
  }
  global.NSProductOptions = {normalize, resolve, resolveFlat, restrictToProduct};
})(window);
(function (global) {
  'use strict';
  let counter = 0;
  const instances = new WeakMap();
  const svgNode = (tag, attributes = {}) => {
    const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, String(value)));
    return node;
  };
  function illustration(key, value, color) {
    const svg = svgNode('svg', {viewBox: '0 0 120 78', width: 120, height: 78, fill: 'none', 'aria-hidden': 'true', focusable: 'false'});
    const line = (d, attributes = {}) => svg.append(svgNode('path', Object.assign({d, stroke: '#4E574C', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round'}, attributes)));
    const rect = (x, y, width, height, attributes = {}) => svg.append(svgNode('rect', Object.assign({x, y, width, height, rx: 2, fill: '#E4E8DF', stroke: '#697363', 'stroke-width': 1.5}, attributes)));
    const circle = (cx, cy, r, attributes = {}) => svg.append(svgNode('circle', Object.assign({cx, cy, r, fill: '#899980'}, attributes)));
    const name = String(value || '').toLowerCase();
    if (key === 'profile_color' || key === 'frame_color') {
      const shade = /^#[0-9a-f]{6}$/i.test(String(color || '')) ? color : '#D1D4CA';
      line('M18 61V17H102V61H18Z M26 53V25H94V53H26Z', {fill: shade, 'fill-rule': 'evenodd', stroke: '#73756D'});
      line('M22 19V57 M98 21V57', {stroke: '#FFFFFF', opacity: 0.35});
    } else if (key === 'fabric_structure') {
      if (name === 'honeycomb') {
        ['M28 18L50 18L60 29L50 40L28 40L18 29Z','M72 18L94 18L104 29L94 40L72 40L62 29Z','M50 40L72 40L82 51L72 62L50 62L40 51Z'].forEach((d) => line(d, {fill: '#E4E8DF'}));
      } else {
        line('M17 58L31 19L45 58L59 19L73 58L87 19L102 58', {'stroke-width': 4});
        line('M17 65H102', {stroke: '#A6B29D'});
      }
    } else if (key === 'mounting' || /^(screw|glue|clamp|wall|ceiling)$/.test(name)) {
      if (/glue|klebe/.test(name)) {
        rect(20, 19, 70, 12, {fill: '#D8CDB5'}); rect(28, 34, 70, 12, {fill: '#E8DEC9'});
        line('M28 46L48 61H97V46 M28 34L20 31', {stroke: '#A58B5B'});
      } else if (/clamp|klemm/.test(name)) {
        rect(43, 13, 25, 51); line('M31 26H78V50H68 M31 26V48H43', {'stroke-width': 5}); line('M80 40H95 M88 33L95 40L88 47');
      } else if (/ceiling|decke/.test(name)) {
        rect(17, 12, 86, 9); line('M39 21V47H81V21 M34 47H86', {'stroke-width': 3}); rect(29, 49, 62, 12); line('M47 23V35 M73 23V35');
      } else if (/wall|wand/.test(name)) {
        rect(20, 12, 11, 54); line('M31 25H77V47 M31 33H69V47', {'stroke-width': 3}); rect(50, 48, 46, 13);
      } else {
        rect(18, 17, 84, 45, {fill: '#EDF0E9'});
        line('M40 29L80 53 M37 28L44 20L51 24L44 32Z M49 35L54 28 M56 39L61 32 M63 43L68 36 M70 47L75 40 M80 53L84 55L82 50', {'stroke-width': 2.5});
      }
    } else if (key === 'opening_direction' || key === 'operating_direction' || key === 'operating_side') {
      rect(27, 10, 66, 58, {fill: '#F1F3ED'}); rect(33, 16, 54, 46, {fill: '#DEE4D7'});
      const right = /right|rechts/.test(name), both = /both|double|beid|mitt|center/.test(name) || (key === 'operating_direction' && name === 'horizontal'), vertical = /top|bottom|oben|unten|vert/.test(name);
      if (vertical) {
        line('M60 24V54 M53 31L60 24L67 31 M53 47L60 54L67 47', {'stroke-width': 3});
      } else if (both) {
        line('M39 39H81 M46 32L39 39L46 46 M74 32L81 39L74 46', {'stroke-width': 3});
      } else {
        line(right ? 'M43 39H78 M70 31L78 39L70 47' : 'M77 39H42 M50 31L42 39L50 47', {'stroke-width': 3});
      }
    } else if (key === 'ladder_type') {
      for (let y = 19; y < 64; y += 12) rect(22, y, 76, 7, {fill: '#D4BE99', stroke: '#95805C'});
      if (name === 'tape') {rect(37, 13, 9, 57, {fill: '#7D745F', stroke: 'none'}); rect(74, 13, 9, 57, {fill: '#7D745F', stroke: 'none'});}
      else line('M41 13V70 M79 13V70', {stroke: '#6B6454', 'stroke-width': 2});
    } else if (key === 'operating_type') {
      rect(30, 15, 50, 9); line('M82 23V53A9 9 0 0 0 100 53V23', {stroke: '#9AA28F'});
      [27, 34, 41, 48, 55].forEach((y) => {circle(82, y, 2); circle(100, y, 2);}); circle(91, 61, 2);
    } else if (key === 'wing_count') {
      rect(21, 11, 78, 56, {fill: '#F1F3ED'});
      if (/double|two|2|doppel/.test(name)) {line('M60 12V66'); circle(54, 40, 2); circle(66, 40, 2);} else {rect(28, 18, 64, 42); circle(84, 40, 2);}
    } else if (key === 'mesh_type') {
      rect(25, 10, 70, 58, {fill: '#F6F7F3'});
      const dense = /pollen/.test(name), step = dense ? 5 : 10;
      for (let x = 30; x < 94; x += step) line('M' + x + ' 11V67', {'stroke-width': dense ? 0.7 : 1, opacity: 0.7});
      for (let y = 16; y < 67; y += step) line('M26 ' + y + 'H94', {'stroke-width': dense ? 0.7 : 1, opacity: 0.7});
      if (dense) {circle(103, 17, 3, {fill: '#BDAB64'}); circle(108, 29, 2, {fill: '#BDAB64'}); circle(103, 40, 3, {fill: '#BDAB64'});}
    } else if (key === 'frame_profile') {
      if (/ramp/.test(name)) line('M17 62H104L82 47H42V62 M42 47V62', {fill: '#DDE3D6', 'stroke-width': 2.5});
      else {const top = /low|9/.test(name) ? 46 : 24; rect(27, top, 66, 62 - top, {fill: '#DDE3D6'}); line('M99 ' + top + 'V62 M95 ' + top + 'H103 M95 62H103');}
    } else if (key === 'closure') {
      if (/magnet/.test(name)) {line('M38 24V44A22 22 0 0 0 82 44V24 M48 24V44A12 12 0 0 0 72 44V24Z', {fill: '#D8DFD1'}); line('M38 29H48 M72 29H82');}
      else {rect(23, 21, 74, 12); for (let x = 28; x < 95; x += 5) line('M' + x + ' 34V59', {'stroke-width': 2});}
    } else {
      rect(27, 10, 66, 58, {fill: '#F1F3ED'}); for (let y = 23; y <= 55; y += 8) line('M34 ' + y + 'H86');
    }
    return svg;
  }
  function mount(source, options = {}) {
    if (!source || source.tagName !== 'SELECT') return null;
    instances.get(source)?.destroy();
    const originalHidden = source.hidden;
    const originalTabIndex = source.getAttribute('tabindex');
    const label = source.labels?.[0] || source.closest('label');
    const labelClone = label?.cloneNode(true);
    labelClone?.querySelectorAll('select, .ns-input-hint').forEach((node) => node.remove());
    const title = options.label || source.dataset.nsTilesLabel || labelClone?.textContent.trim() || source.getAttribute('aria-label') || '';
    const group = document.createElement('fieldset'); group.className = 'ns-choice-field'; group.dataset.nsChoiceField = '';
    const legend = document.createElement('legend'); legend.id = 'NSChoiceLegend-' + (++counter); legend.textContent = title;
    const grid = document.createElement('div'); grid.className = 'ns-choice-grid'; grid.setAttribute('role', 'radiogroup'); grid.setAttribute('aria-labelledby', legend.id);
    if (source.required) grid.setAttribute('aria-required', 'true');
    group.append(legend, grid);
    const key = options.key || source.dataset.nsChoiceKey || source.dataset.nsOption || (source.hasAttribute('data-ns-3d-mounting') || source.hasAttribute('data-ns-mounting') ? 'mounting' : 'variant');
    const buttons = [];
    [...source.options].filter((option) => option.value !== '').forEach((option) => {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'ns-choice-tile'; button.setAttribute('role', 'radio'); button.dataset.nsChoiceValue = option.value;
      button.disabled = source.disabled || option.disabled;
      const visual = document.createElement('span'); visual.className = 'ns-choice-visual'; visual.setAttribute('aria-hidden', 'true');
      const image = option.dataset.imageUrl;
      if (image) {const img = document.createElement('img'); img.src = image; img.alt = ''; img.width = 120; img.height = 90; img.loading = 'lazy'; visual.append(img);}
      else visual.append(illustration(key, option.value, option.dataset.color));
      const text = document.createElement('span'); text.className = 'ns-choice-name'; text.textContent = option.textContent.trim();
      button.append(visual, text); grid.append(button); buttons.push(button);
      button.addEventListener('click', () => {
        if (button.disabled) return;
        source.value = option.value;
        source.dispatchEvent(new Event('input', {bubbles: true}));
        source.dispatchEvent(new Event('change', {bubbles: true}));
        sync();
      });
      button.addEventListener('keydown', (event) => {
        const enabled = buttons.filter((entry) => !entry.disabled);
        const current = enabled.indexOf(button);
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % enabled.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current + enabled.length - 1) % enabled.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = enabled.length - 1;
        if (next === undefined || !enabled.length) return;
        event.preventDefault(); enabled[next].click(); enabled[next].focus();
      });
    });
    const sync = () => {
      const active = buttons.find((button) => button.dataset.nsChoiceValue === source.value);
      buttons.forEach((button) => {const checked = button === active; button.setAttribute('aria-checked', String(checked)); button.tabIndex = checked || (!active && button === buttons.find((entry) => !entry.disabled)) ? 0 : -1;});
      grid.setAttribute('aria-invalid', String(source.required && !source.value));
    };
    const onInvalid = (event) => {event.preventDefault(); grid.setAttribute('aria-invalid', 'true'); buttons.find((button) => !button.disabled)?.focus();};
    source.addEventListener('change', sync); source.addEventListener('invalid', onInvalid);
    if (label?.contains(source)) {label.after(group); label.hidden = true;}
    else {source.after(group); if (label) label.hidden = true;}
    const notice = label?.querySelector('.ns-input-hint');
    if (notice) {const copy = notice.cloneNode(true); group.append(copy);}
    source.hidden = true; source.tabIndex = -1; source.setAttribute('aria-hidden', 'true');
    const instance = {sync, destroy() {group.remove(); source.removeEventListener('change', sync); source.removeEventListener('invalid', onInvalid); source.hidden = originalHidden; source.removeAttribute('aria-hidden'); if (originalTabIndex === null) source.removeAttribute('tabindex'); else source.setAttribute('tabindex', originalTabIndex); if (label) label.hidden = false; instances.delete(source);}};
    instances.set(source, instance); sync(); return instance;
  }
  function enhance(scope) {
    scope.querySelectorAll('select[data-ns-option], select[data-ns-mounting], select[data-ns-variant], select[data-ns-choice-source]').forEach((source) => {
      if (!source.hasAttribute('data-ns-swatch-source') && !instances.has(source)) mount(source);
    });
  }
  global.NSChoiceTiles = {mount, enhance, illustration};
})(window);
